<template>
  <div v-if="visible"  class="magic-ui-select" :class="{labelTop: config.labelStyle.location == 'top', labelLeft: config.labelStyle.location == 'left'}">
    <div class="input-label" v-if="config.label.text"
    :style="{
      '--label-margin': config.labelStyle.margin,
      '--label-color': config.labelStyle.color,
      '--label-font-size': config.labelStyle.fontSize,
      '--label-font-weight': config.labelStyle.fontWeight,
      '--label-width': config.labelStyle.width,
      '--label-align': config.labelStyle.align,
      }"
    ><span v-if="config.verification.required" style="color: red">*</span><span>{{config.label.text}}</span></div>
    <div ref="inputRef" class="input-wrap">
    <el-select
      
      v-model="selectValue"
      @change="selectChange"
      @focus="selectFocus"
      @blur="selectBlur"
      @visible-change="visibleChange"
      :placeholder="config.placeholder"
      :disabled="config.disabled"
      :readonly="config.readonly"
      :clearable="config.clearable"
      :filterable="config.filterable"
      :style="{
        '--el-select-bg-color': config.inputStyle.backgroundColor,
        '--el-select-border-color': config.inputStyle.borderColor,
        '--el-select-border-radius': config.inputStyle.borderRadius,
        '--el-select-text-color': config.inputStyle.color,
        '--el-select-focus-border-color': config.inputStyle.borderFocusColor,
        '--el-select-input-height': config.inputStyle.height
      }"
    >
      <template v-if="selectOptions && selectOptions.length">
        <el-option
          v-for="item in selectOptions"
          v-show="!item.hidden"
          :key="item.value"
          :label="item.label"
          :value="item.value"
          :disabled="item.disabled"
        >
        </el-option>
      </template>
    </el-select>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { reactive, ref, watch, watchEffect, computed, inject } from 'vue';
import { MComponent } from '@tmagic/schema';
import { EOptionsSource, Option, AutoOption } from '../types';
import useApp from '@/hooks/useApp';
import initValue from '../initValue';
import useDisplay from '@/hooks/useDisplay';
import useErrorTips from '../../../useErrorTips';
import { ElSelect, ElOption } from 'element-plus';
import 'element-plus/es/components/select/style/css';

const props = defineProps<{
  config: MComponent & typeof initValue;
  outLabelWidth?: string | number;
}>();

const app = useApp(props);
const node = app?.page?.getNode(props.config.id!);

const selectValue = ref(props.config.defaultValue);
const inputRef = ref();
const errorTips = ref('');

useErrorTips(errorTips, inputRef, props.config);

watch(
  () => props.config.defaultValue,
  (val, oldVal) => {
    if (selectValue.value === oldVal) {
      selectValue.value = val;
    }
  }
);

const selectOptions = computed(() => {
  let options: Array<Option> = [];
  if (props.config.optionsSource === EOptionsSource.manual) {
    options = props.config?.manualOptions?.map((option: Option) => {
      return {
        label: app?.runScript(option.label),
        value: app?.runScript(option.value),
        hidden: app?.runScript(option.hidden),
        disabled: app?.runScript(option.disabled) || false,
      };
    });
  } else {
    const configOptions: AutoOption = props.config?.autoOptions;
    options = app?.runScript(configOptions?.dataSource);
    options = options?.filter?.((option: Option, index: number) => {
      option.label = app?.runScript(configOptions?.label, {
        item: option,
        i: index,
      });
      option.value = app?.runScript(configOptions?.value, {
        item: option,
        i: index,
      });
      option.disabled =
        app?.runScript(configOptions?.disabled, { item: option, i: index }) ||
        false;
      option.hidden = app?.runScript(configOptions?.hidden, {
        item: option,
        i: index,
      });
      return !option.hidden;
    });
  }
  return options || [];
});

const { visible, show, hide } = useDisplay(props);



const rules = reactive<any[]>([]);
watchEffect(() => {
  rules.splice(0, rules.length);
  const { required, customRule } = props.config.verification;
  if (required) {
    rules.push({
      validator(rule: any, value: any, callback: any) {
        if (
          selectValue.value === undefined ||
          selectValue.value === null ||
          selectValue.value === ''
        ) {
          return '必填项';
        }
      },
    });
  }
  if (customRule && typeof customRule === 'function') {
    rules.push({
      validator: (rule: any, value: any, callback: any) => {
        const result = customRule(selectValue.value);
        return result;
      },
    });
  }
});
const validate = async () => {
  for (const rule of rules) {
    const tips = await rule.validator();
    if (tips) {
      errorTips.value = tips;
      return false;
    }
  }
  errorTips.value = '';
  return true;
};

const selectChange = (val: any) => {
  console.log(`组件${props.config.id}的值发生了变化，新值为${val}`);
  app?.emit('change', node, val);
  validate();
};
const selectFocus = () => {
  console.log(`组件${props.config.id}触发了focus事件`);
  app?.emit('focus', node);
};
const selectBlur = () => {
  console.log(`组件${props.config.id}触发了blur事件`);
  app?.emit('blur', node);
};
const visibleChange = () => {
  console.log(`组件${props.config.id}触发了VisibleChange事件`);
  app?.emit('visibleChange', node);
};

const clearValidate = () => {
  errorTips.value = '';
};

const clearValue = () => {
  selectValue.value = '';
};

const setValue = (val: string) => {
  selectValue.value = val;
};

const useFormField = inject<any>('useFormField', undefined);
if (useFormField) {
  useFormField({
    fieldPropsConfig: props.config,
    fieldValueRef: selectValue,
    validate,
    clearValidate,
  });
}

// 暴露给编辑器的接口请注册在这里
const exportInterface = reactive({
  errorMessage: errorTips,
  clearValue,
  setValue,
  value: selectValue,
  show,
  hide,
  validate,
});

defineExpose(exportInterface);
</script>

<style lang="scss" scoped>
.magic-ui-select {
  .input-label {
    flex-shrink: 0;
    font-size:var(--label-font-size);
    color: var(--label-color);
    font-weight: var(--label-font-weight);
    min-width: var(--label-width);
    justify-content: var(--label-align);
    display: flex;
  }
  &.labelTop {
    display: inline-block;
    .input-label {
      margin-bottom: var(--label-margin);
    }
  }
  &.labelLeft {
    display: flex;
    .input-label {
      margin-right: var(--label-margin);
    }
  }
  .input-wrap {
    flex: 1;
    display: flex;
  }
  align-items: center;
  :deep(.el-form-item__label) {
    justify-content: var(--label-justify-content);
    color: var(--el-label-color);
    font-weight: var(--el-form-label-font-weight);
  }
  :deep(.el-select) {
    flex: 1;
  }
  :deep(.el-input) {
    --el-input-bg-color: var(--el-select-bg-color);
    --el-input-border-color: var(--el-select-border-color);
    --el-input-border-radius: var(--el-select-border-radius);
    --el-input-text-color: var(--el-select-text-color);
    --el-input-height: var(--el-select-input-height);
    --el-select-input-focus-border-color: var(--el-select-focus-border-color);
  }
}
.errorTip {
  border-color: 1px solid red;
}
.error-tips {
  color: red;
  margin-top: 2px;
  font-size: 12px;
}
</style>
